import * as React from 'react';
import Box from '@mui/material/Box';
import Tab, { TabProps } from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList, { TabListProps } from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { styled, SxProps, Theme } from '@mui/material/styles';
import { Grid, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { GoalType } from '../GoalType/GoalType';
import { GoalTypeEnum } from 'common/types';

type VariantType = {
    variant: TabVariant;
};

// Styling-only props that must not be forwarded to the underlying DOM node.
const TAB_LIST_STYLING_PROPS = ['showBorderBottom', 'enclosed'];

export const StyledTabList = styled(TabList, {
    shouldForwardProp: (prop) => !TAB_LIST_STYLING_PROPS.includes(prop as string)
})<TabListProps & { showBorderBottom: boolean; enclosed?: boolean }>(({ showBorderBottom, enclosed, theme }) => ({
    display: 'flex',
    width: '100%',
    ...(enclosed
        ? {
              '& .MuiTabs-scroller': {
                  overflow: 'visible !important'
              },
              '& .MuiTabs-flexContainer': {
                  borderBottom: `1px solid ${theme.palette.semantic.int.selected}`
              }
          }
        : {
              borderBottom: showBorderBottom ? `1px solid ${theme.palette.neutral[200]}` : '0'
          })
}));

const StyledTab = styled(Tab)<TabProps & VariantType>(({ theme, variant }) => ({
    ...(variant === TabVariant.PRIMARY_TAB && {
        '&.Mui-selected': {
            backgroundColor: theme.palette.primary[100]
        },
        color: theme.palette.primary.dark
    }),
    ...(variant === TabVariant.GOALS_TAB && {
        '&:not(:first-child)': {
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '8px',
                height: '32px',
                width: '1px',
                backgroundColor: '#ccc'
            }
        },
        '&:first-child': {
            paddingLeft: 0,
            maxWidth: '168px'
        },
        color: theme.palette.secondary.main,
        '&.Mui-selected': {
            position: 'relative',
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                margin: '0 18px',
                height: '2px',
                backgroundColor: theme.palette.primary.dark
            },
            '&:first-child::after': {
                marginLeft: 0
            },
            color: theme.palette.primary.dark
            //borderBottom: `2px solid ${theme.palette.primary.dark}`
        },
        maxWidth: '186px',
        alignItems: 'flex-start',
        padding: '0 18px'
    }),
    ...(variant === TabVariant.COMPACT_TAB && {
        color: theme.palette.secondary.main,
        '&.Mui-selected': {
            color: theme.palette.primary.dark
        },
        padding: 0,
        margin: 0,
        marginRight: theme.spacing(4),
        maxWidth: 'max-content',
        alignItems: 'flex-start'
    }),
    ...(variant === TabVariant.ENCLOSED_TAB && {
        color: theme.palette.neutral[700],
        fontWeight: 700,
        border: `1px solid ${theme.palette.semantic.bd.default}`,
        borderBottom: 'none',
        borderTopLeftRadius: theme.spacing(0.5),
        borderTopRightRadius: theme.spacing(0.5),
        padding: theme.spacing(2, 4),
        marginRight: theme.spacing(0.5),
        maxWidth: 'max-content',
        minHeight: 'auto',
        alignItems: 'center',
        position: 'relative',
        overflow: 'visible',
        '&.Mui-selected': {
            color: theme.palette.semantic.text.default,
            backgroundColor: theme.palette.semantic.bg.default,
            borderTop: `6px solid ${theme.palette.semantic.int.selected}`,
            borderLeft: `1px solid ${theme.palette.semantic.int.selected}`,
            borderRight: `1px solid ${theme.palette.semantic.int.selected}`,
            borderBottom: 'none',
            zIndex: 1,
            '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: '-1px',
                height: '2px',
                backgroundColor: theme.palette.semantic.bg.default,
                zIndex: 2
            }
        }
    }),
    fontSize: variant === TabVariant.ENCLOSED_TAB ? '1rem' : '1.25rem',
    textTransform: 'none',
    flexGrow: variant === TabVariant.ENCLOSED_TAB ? 0 : 1
}));

const StyledLimitedText = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%'
});

export enum TabVariant {
    // eslint-disable-next-line no-unused-vars
    PRIMARY_TAB = 'primary',
    // eslint-disable-next-line no-unused-vars
    GOALS_TAB = 'goalsTab',
    // eslint-disable-next-line no-unused-vars
    COMPACT_TAB = 'compactTab',
    // eslint-disable-next-line no-unused-vars
    ENCLOSED_TAB = 'enclosedTab'
}

export interface TabsProps {
    tabPanels: {
        enabled?: boolean;
        title: string;
        hidden: boolean;
        children: React.ReactNode;
        type?: GoalTypeEnum;
        needFix?: boolean;
    }[];
    value?: string;
    // eslint-disable-next-line no-unused-vars
    onChange?: (event: React.SyntheticEvent, newValue: string) => void;
    variant?: TabVariant;
    panelSx?: SxProps<Theme>;
}

const GoalsTab = ({
    title,
    type,
    needFix,
    shorten
}: {
    title: string;
    type?: GoalTypeEnum;
    needFix: boolean;
    shorten: boolean;
}) => {
    const theme = useTheme();
    return (
        <Grid display="flex" alignItems="center" gap={1}>
            <Grid display="flex" alignItems="center" justifyContent="center" width={32} height={32} borderRadius="50%">
                {type && <GoalType type={type} size="small" />}
            </Grid>
            {shorten ? (
                <StyledLimitedText>{title.length > 6 ? title.slice(0, 6) + '...' : title}</StyledLimitedText>
            ) : (
                <span>{title}</span>
            )}

            {needFix && <InfoIcon sx={{ width: '16px', height: '16px', color: theme.palette.warning[500] }} />}
        </Grid>
    );
};

export const Tabs = ({ tabPanels, onChange, value = '0', variant = TabVariant.PRIMARY_TAB, panelSx }: TabsProps) => {
    return (
        <Box sx={{ width: '100%', typography: 'body1' }}>
            <TabContext value={value}>
                <StyledTabList
                    onChange={onChange}
                    aria-label="tab lists"
                    showBorderBottom={variant === TabVariant.COMPACT_TAB}
                    enclosed={variant === TabVariant.ENCLOSED_TAB}
                    TabIndicatorProps={{
                        sx: {
                            display:
                                variant === TabVariant.GOALS_TAB || variant === TabVariant.ENCLOSED_TAB
                                    ? 'none'
                                    : 'block'
                        }
                    }}
                >
                    {tabPanels
                        .filter(({ hidden }) => !hidden)
                        .map(({ title, enabled, type, needFix }, index) => {
                            return (
                                <StyledTab
                                    variant={variant}
                                    label={
                                        variant === TabVariant.PRIMARY_TAB ||
                                        variant === TabVariant.COMPACT_TAB ||
                                        variant === TabVariant.ENCLOSED_TAB ? (
                                            title
                                        ) : (
                                            <GoalsTab title={title} type={type} needFix={!!needFix} shorten />
                                        )
                                    }
                                    key={`${title}-${index}`}
                                    value={index.toString()}
                                    disabled={!enabled}
                                />
                            );
                        })}
                </StyledTabList>

                {tabPanels
                    .filter(({ hidden }) => !hidden)
                    .map(({ children, title }, index) => (
                        <TabPanel key={`${title}-${index}`} value={index.toString()} sx={panelSx}>
                            {children}
                        </TabPanel>
                    ))}
            </TabContext>
        </Box>
    );
};
